import angular from "angular";
import htmlTemplate from "./attachment_gallery.html";
import _ from "lodash";
import {
    DocumentAttachment,
    ImageAttachment,
} from "./gallery_attachment.model";
import PresentableError from "../../../errors/PresentableError";

/**
 * Default supported file extensions for attachments
 * @type {Set<string>}
 */
const FILE_EXTENSIONS = new Set([".png", ".jpg", ".jpeg", ".gif", ".pdf"]);
const SUPPORTED_MIME_TYPES = new Set([
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/gif",
]);

export default angular
    .module("sbApp.common.components.attachmentGallery", [])
    .component("sbAttachmentGallery", {
        templateUrl: htmlTemplate,
        bindings: {
            attachmentsVisible: "<?",
            attachments: "<",
            filterExtensions: "<",
            onDelete: "&?",
            onAdd: "&?",
            isUploading: "&?",
        },
        controllerAs: "gallery",
        controller: function AttachmentGalleryController(
            $sbImagesOverlay,
            $sbFilesApi,
            $q,
            Analytics,
            $sbImageApi,
            downloadCenterService,
            $sbCurrentProject,
            $sbErrorPresenter,
            $sbImageUpload,
            $element,
            $scope
        ) {
            const DEFAULT_GALLERY_SIZE = 6;

            const vm = this;

            let _previousEvent;
            let _dragCounter = 0;
            let _dragEventHandlers = null;

            vm.$onInit = $onInit;
            vm.$onChanges = $onChanges;
            vm.$onDestroy = $onDestroy;

            vm.isShowable = !vm.onAdd; // by definition when adding is enabled "showing" is disabled
            vm.show = showAttachment;
            vm.scrollRight = scrollRight;
            vm.scrollLeft = scrollLeft;
            vm.isArrowRightActive = isArrowRightActive;
            vm.isArrowLeftActive = isArrowLeftActive;
            vm.onAttachFile = onAttachFile;
            vm.numberOfUploads = 0;

            vm.isDragOver = false;

            vm.isImage = (attachment) => attachment instanceof ImageAttachment;
            vm.isDocument = (attachment) =>
                attachment instanceof DocumentAttachment;

            /**
             * Filter the [FILE_EXTENSIONS]
             * @type {*|(function(*): *)}
             */
            vm.filterExtensions =
                vm.filterExtensions ||
                function (params) {
                    return params;
                };

            function $onInit() {
                vm.photoOffset = 0;

                vm.attachmentsVisible =
                    vm.attachmentsVisible || DEFAULT_GALLERY_SIZE;
                vm.photoSize = Math.ceil(100 / vm.attachmentsVisible) + "%";
                if (!vm.isShowable) {
                    vm.attachmentsVisible--;
                }
                vm.ellipsedTextMaxSize = 80;
                vm.allowedExtension = vm.filterExtensions(
                    Array.from(FILE_EXTENSIONS)
                );

                _initDragAndDrop();
            }

            function $onDestroy() {
                _destroyDragAndDrop();
            }

            function $onChanges(changes) {
                if (!changes.attachments) {
                    return;
                }

                if (
                    _.get(changes, "attachments.currentValue") ===
                    _.get(changes, "attachments.previousValue")
                ) {
                    return;
                }

                (changes.attachments.currentValue || [])
                    .filter(this.isImage)
                    .filter((image) => !image.isThumbDownloaded())
                    .forEach((image) => {
                        downloadThumbImage(image.id).then((dataUri) => {
                            image.thumbDataUri = dataUri;
                        });
                    });
            }

            function downloadThumbImage(imageId) {
                const projectId = $sbCurrentProject.pick("id");
                return $sbImageApi
                    .download(projectId, imageId, {
                        transformResponse: [
                            $sbImageApi.response.transformToDataURL,
                        ],
                        query: {
                            fit: "thumb",
                        },
                    })
                    .then((image) => image.dataUrl)
                    .catch($sbErrorPresenter.catch);
            }

            function onAttachFile($event, file) {
                // This solves the IE duplicating attachments issue
                if (
                    _previousEvent &&
                    _previousEvent.hwTimestamp &&
                    _previousEvent.hwTimestamp === $event.hwTimestamp
                ) {
                    return false;
                } else {
                    _previousEvent = $event;
                }
                // in line with SUPPORTED_MIME_TYPES
                switch (file.type) {
                    case "application/pdf":
                        return onAttachDocument($event, file);
                    case "image/jpeg":
                    case "image/png":
                    case "image/gif":
                        return onAttachImage($event, file);
                    default:
                        throw new Error("Unsupported MIME type: " + file.type);
                }
            }

            function onAttachDocument($event, file) {
                const fileSizeInMB = file.size / 1024 / 1024;
                if (fileSizeInMB > 50) {
                    $sbErrorPresenter.catch(
                        new PresentableError("", "", "PDF_EXCEEDS_LIMIT")
                    );
                    return;
                }
                const upload = $sbFilesApi
                    .upload($sbCurrentProject.pick("id"), file, {
                        filename: file.name,
                    })
                    .then(
                        (asset) => new DocumentAttachment(asset.id, file.name)
                    );

                return _doAttach($event, upload);
            }

            function onAttachImage($event, file) {
                Analytics.trackEvent(
                    "sb_image_attachment",
                    "file type",
                    file.type
                );

                const upload = $sbImageUpload
                    .transformAndUploadImage($sbCurrentProject.pick("id"), file)
                    .then((image) => new ImageAttachment(image.id));

                return _doAttach($event, upload);
            }

            function _doAttach($event, uploadPromise) {
                onImageUploadStart();
                return uploadPromise
                    .then((newAttachment) => {
                        return vm.onAdd({
                            $event: $event,
                            attachment: newAttachment,
                        });
                    })
                    .catch((err) => {
                        $sbErrorPresenter.catch(err); // handle the error
                    })
                    .finally(() => {
                        return onImageUploadFinished();
                    });
            }

            function onImageUploadStart() {
                vm.isUploading({
                    isUploading: true,
                });
                vm.numberOfUploads++;
                return vm.numberOfUploads;
            }

            function onImageUploadFinished() {
                if (vm.numberOfUploads > 0) {
                    vm.numberOfUploads--;
                }
                if (vm.numberOfUploads < 1) {
                    vm.isUploading({
                        isUploading: false,
                    });
                }
                return vm.numberOfUploads;
            }

            function showAttachment($event, attachment) {
                if (!vm.isShowable) {
                    return;
                }

                if (attachment instanceof ImageAttachment) {
                    return openPhoto($event, attachment);
                }
                if (attachment instanceof DocumentAttachment) {
                    return downloadAttachment($event, attachment);
                }
            }

            function openPhoto($event, attachment) {
                // open the overlay showing images only.
                //  -> focus should be the clicked image.
                const assetId = attachment.id;
                const images = vm.attachments
                    .filter(
                        (attachment) => attachment instanceof ImageAttachment
                    )
                    .map((image) => {
                        return {
                            id: image.id,
                        };
                    });
                const indexOfPhoto = _.findIndex(images, ["id", assetId]);

                $sbImagesOverlay.open($event, images, indexOfPhoto);
            }

            function downloadAttachment($event, attachment) {
                return $sbFilesApi
                    .download($sbCurrentProject.pick("id"), attachment.id, {})
                    .then((xhr) =>
                        downloadCenterService.downloadFrom(
                            xhr,
                            (backendFilename) =>
                                backendFilename || attachment.name
                        )
                    )
                    .catch($sbErrorPresenter.catch);
            }

            function _initDragAndDrop() {
                if (vm.isShowable) {
                    return;
                }

                const element = $element[0];

                function onDragEnter(event) {
                    event.preventDefault();
                    _dragCounter++;
                    // Only trigger a digest on the first enter to avoid redundant cycles
                    // when entering nested child elements.
                    if (_dragCounter === 1) {
                        $scope.$apply(function () {
                            vm.isDragOver = true;
                        });
                    }
                }

                function onDragOver(event) {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "copy";
                }

                function onDragLeave() {
                    // Clamp at 0 to guard against browser events firing out of order
                    // (e.g. extra dragleave after a drop resets the counter).
                    if (_dragCounter > 0) {
                        _dragCounter--;
                    }
                    if (_dragCounter === 0) {
                        $scope.$apply(function () {
                            vm.isDragOver = false;
                        });
                    }
                }

                function onDrop(event) {
                    event.preventDefault();
                    _dragCounter = 0;

                    // Directories have an empty MIME type; unsupported types are
                    // silently ignored so the drag path matches the file-picker
                    // validation UX (only valid types are processed).
                    const files = Array.from(event.dataTransfer.files).filter(
                        (file) => SUPPORTED_MIME_TYPES.has(file.type)
                    );

                    $scope.$apply(function () {
                        vm.isDragOver = false;
                        files.forEach(function (file) {
                            onAttachFile(event, file);
                        });
                    });
                }

                element.addEventListener("dragenter", onDragEnter);
                element.addEventListener("dragover", onDragOver);
                element.addEventListener("dragleave", onDragLeave);
                element.addEventListener("drop", onDrop);

                _dragEventHandlers = {
                    dragenter: onDragEnter,
                    dragover: onDragOver,
                    dragleave: onDragLeave,
                    drop: onDrop,
                };
            }

            function _destroyDragAndDrop() {
                if (!_dragEventHandlers) {
                    return;
                }
                const element = $element[0];
                Object.keys(_dragEventHandlers).forEach(function (eventName) {
                    element.removeEventListener(
                        eventName,
                        _dragEventHandlers[eventName]
                    );
                });
                _dragEventHandlers = null;
            }

            function scrollRight() {
                if (isArrowRightActive()) {
                    vm.photoOffset++;
                }
            }

            function scrollLeft() {
                if (isArrowLeftActive()) {
                    vm.photoOffset--;
                }
            }

            function isArrowRightActive() {
                return (
                    vm.attachments &&
                    vm.attachments.length >
                        vm.photoOffset +
                            vm.attachmentsVisible -
                            vm.numberOfUploads
                );
            }

            function isArrowLeftActive() {
                return vm.photoOffset > 0;
            }
        },
    });
